import React, {
  useEffect, useRef, createContext,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Theme,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';

import { Button } from '../../..';
import { CheckIcon, TimesIcon } from '../../../icon/solid';
import { TrashAltIcon, CopyIcon } from '../../../icon/regular';
import { useId } from '../../../../hooks';
import { modalStyles } from './styles';
import hotKeyConverter from '../../../../utility/keyCodeConverter';
import { ButtonConfirm } from '.';
import AdvancedActionCell from '../../../action-cell/AdvancedActionCell';
import Action from '../../../action/Action';

type ModalComponentContextType = {
  modalContentRef: React.MutableRefObject<HTMLElement | null>;
};
const initialModalComponentContextState = {
  modalContentRef: { current: null },
};
export const ModalComponentContext = createContext<ModalComponentContextType>(initialModalComponentContextState);

export const useModalComponentContext = () => {
  const modalContentRef = useRef<HTMLElement | null>(null);

  return {
    modalContentRef,
  };
};

type Props = {
  open: boolean;
  cancelHandler: () => void;
  saveHandler: () => void;
  deleteHandler: () => void;
  children: React.ReactNode;
  title: React.ReactNode;
  duplicate?: (()=> void) | null;
  showKebabMenu?: boolean;
};

const ModalComponent = ({
  open, showKebabMenu, saveHandler, cancelHandler, duplicate, children, title, deleteHandler,
}: Props) => {
  const id = useId();
  const classes = modalStyles();
  const theme = useTheme<Theme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const modalComponentContext = useModalComponentContext();

  useEffect(() => {
    const saveHotKey = (event: KeyboardEvent) => {
      if (event.keyCode === hotKeyConverter('enter')) {
        event.preventDefault();
        event.stopPropagation();
        saveHandler();
      }
    };
    window.addEventListener('keydown', saveHotKey);
    return () => window.removeEventListener('keydown', saveHotKey);
  }, [saveHandler]);

  return (
    <ModalComponentContext.Provider value={modalComponentContext}>
      <Dialog
        open={open}
        onEscapeKeyDown={cancelHandler} // closeHandler
        aria-labelledby={id}
        disableBackdropClick
        PaperProps={{ className: classes.modal }}
        fullWidth
        style={{ zIndex: 2147483100 }}
        fullScreen={fullScreen}
      >
        <Grid container id={id} className={classes.header} justify="space-between" alignItems="center">
          <Grid item className={classes.title}>
            {title}
          </Grid>
          <Grid item>
            {
              showKebabMenu
              && (
                <AdvancedActionCell>
                  <ButtonConfirm
                    onConfirm={deleteHandler}
                    icon={<TrashAltIcon className={classes.icon} />}
                    title="Remove"
                    className={clsx(classes.removeButton, classes.panelButton)}
                  />
                  {duplicate &&
                  <Action icon={<CopyIcon className={classes.icon} />} title="Duplicate" onClick={duplicate} />
                  }
                </AdvancedActionCell>
              )

            }
            <IconButton aria-label="close" className={classes.closeButton} onClick={cancelHandler /* closeHandler */}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        <DialogContent ref={modalComponentContext.modalContentRef} className={classes.dialogContent}>
          {children}
        </DialogContent>
        <DialogActions>
          <Grid container justify="space-between" spacing={3} style={{ margin: 'unset' }}>
            <Grid item>
              <Button
                onClick={cancelHandler}
                className={clsx(classes.cancelButton, classes.panelButton)}
                variant="contained"
                color="default"
              >
                <TimesIcon className={classes.buttonIcon} />
                <span>Cancel</span>
              </Button>
            </Grid>
            <Grid item>
              <Button
                onClick={saveHandler}
                className={classes.panelButton}
                variant="contained"
                color="primary"
              >
                <CheckIcon className={classes.buttonIcon} />
                <span>Save</span>
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </ModalComponentContext.Provider>
  );
};

export default ModalComponent;
